import * as React from "react";
import { useRef, useEffect } from "react";
import SpreetailHeader from "../layout/SpreetailHeader";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useSmartColumn } from "./useSmartColumn";
import ProgressSheet from "../common/ProgressSheet";

const SmartColumn: React.FC = () => {
  const {
    selectedSource,
    dropdownOpen,
    setDropdownOpen,
    showValidation,
    tableOptions,
    isLoadingOptions,
    userIntent,
    setUserIntent,
    isGenerating,
    suggestions,
    selectedSuggestions,
    isInserting,
    error,
    handleSourceSelect,
    toggleSuggestion,
    handleGenerate,
    handleInsertColumns,
    progress,
    isLoadingProgress,
  } = useSmartColumn();

  const isBusy =
    isLoadingOptions || isGenerating || isLoadingProgress;

  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [error]);

  return (
    <>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .bg-gradient-custom {
            background: linear-gradient(180deg, #D3E8D9 0%, #EEEEEE 79.45%);
          }
          .bg-white-90 {
            background-color: rgba(255, 255, 255, 0.6);
          }
          .dropdown-scroll {
            overflow-y: auto;
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .dropdown-scroll::-webkit-scrollbar {
            display: none;
          }
          .textarea-scroll {
            overflow-y: auto;
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .textarea-scroll::-webkit-scrollbar {
            display: none;
          }
          .suggestions-scroll {
            overflow-y: auto;
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .suggestions-scroll::-webkit-scrollbar {
            display: none;
          }
          textarea::placeholder {
            color: #9CA3AF;
            opacity: 0.7;
          }
        `}
      </style>
      <div className="h-screen flex flex-col relative font-sans text-gray-800 bg-gradient-custom">
        <div
          className="absolute -right-10 -top-10 w-50 h-50 blur-[40px] pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at center, rgba(134, 239, 172, 0.3), transparent 70%)",
          }}
        ></div>
        <div
          className="absolute -left-8 -bottom-8 w-38 h-38 blur-[40px] pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at center, rgba(167, 243, 208, 0.3), transparent 70%)",
          }}
        ></div>

        <SpreetailHeader />

        <div className="flex flex-col items-center p-5 flex-1 overflow-y-auto">
          <div className="w-full max-w-md">
            <div className="text-left mb-5">
              <h1 className="text-lg font-medium leading-none text-gray-800 m-0 mb-3">
                Smart Column
              </h1>
            </div>

            {suggestions.length === 0 ? (
              <>
                <div className="mb-4">
                  <label className="text-[13px] font-semibold text-gray-800 mb-1.5 block">
                    Select Source
                  </label>

                  <p className="text-[13px] text-gray-500 m-0 mb-3">
                    Choose a table or sheet to add new columns
                  </p>

                  {isLoadingOptions && (
                    <div className="bg-blue-50 rounded-md p-2 px-3 mb-4 flex items-center gap-2">
                      <div
                        className="w-[18px] h-[18px] border-2 border-blue-500 border-t-transparent rounded-full"
                        style={{ animation: "spin 1s linear infinite" }}
                      ></div>
                      <p className="text-xs text-blue-800 m-0">Loading data sources...</p>
                    </div>
                  )}

                  {showValidation && !selectedSource && (
                    <div className="text-[11px] text-red-600 mb-1.5">Please select a Source</div>
                  )}
                  <div className="relative mb-5">
                    <button
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                      className="w-full p-2.5 px-3 border border-gray-200 rounded-md bg-white-90 text-[13px] text-left cursor-pointer flex justify-between items-center overflow-hidden"
                      style={{ color: selectedSource ? "#1f2937" : "#9CA3AF" }}
                      title={selectedSource || "Select table"}
                      disabled={isLoadingOptions}
                    >
                      <span className="truncate mr-2">{selectedSource || "Select table"}</span>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        className={`transition-transform duration-200 flex-shrink-0 ${dropdownOpen ? "rotate-180" : "rotate-0"}`}
                      >
                        <path
                          d="M4 6L8 10L12 6"
                          stroke="#6B7280"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>

                    {dropdownOpen && (
                      <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-md z-10 mt-1 max-h-48 dropdown-scroll">
                        {tableOptions.length === 0 ? (
                          <div className="p-2.5 px-3 text-[13px] text-gray-500 text-center">
                            No data sources available
                          </div>
                        ) : (
                          tableOptions.map((option, index) => (
                            <button
                              key={index}
                              onClick={() => handleSourceSelect(option)}
                              className="w-full p-2.5 px-3 border-none bg-transparent text-[13px] text-gray-800 text-left cursor-pointer hover:bg-gray-50 overflow-hidden"
                              style={{
                                borderBottom:
                                  index < tableOptions.length - 1 ? "1px solid #F3F4F6" : "none",
                              }}
                              title={option}
                            >
                              <span className="truncate block">{option}</span>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* User Intent - Card Container */}
                <div className="mb-4 w-full rounded-lg p-4 border border-gray-200" style={{ backgroundColor: 'rgba(255, 255, 255, 0.6)' }}>
                  <label className="text-[13px] font-semibold text-gray-800 mb-1 block">
                    User Intent
                  </label>

                  <p className="text-[12px] text-gray-500 m-0 mb-3">
                    Describe what kind of columns you want
                  </p>

                  <textarea
                    value={userIntent}
                    onChange={(e) => setUserIntent(e.target.value)}
                    disabled={isGenerating}
                    placeholder='Eg: Calculate profitability metrics'
                    className={`w-full h-[100px] p-3 border-none rounded-md bg-white text-[13px] resize-none outline-none box-border textarea-scroll ${
                      isGenerating ? "text-gray-500 bg-gray-50" : "text-gray-800"
                    }`}
                  />
                </div>

                {error && (
                  <div ref={errorRef} className="bg-red-100 rounded-md p-3 mb-4 flex items-start gap-2">
                    <div className="w-5 h-5 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-sm font-bold">!</span>
                    </div>
                    <p className="text-[13px] text-red-800 m-0 leading-snug">
                      {error}
                    </p>
                  </div>
                )}

                {/* ✅ PROGRESS SHEET OVERLAY */}
                <ProgressSheet
                  isOpen={isLoadingProgress && progress !== undefined && progress !== null}
                  progress={progress ?? 0}
                  title="Generating columns"
                  subtitle="Please wait while we generate smart column suggestions"
                />
              </>
            ) : (
              <>
                {/* Parent Card - Transparent container wrapping everything */}
                <div className="rounded-lg border border-gray-200 p-4" style={{ backgroundColor: 'rgba(255, 255, 255, 0.6)' }}>
                  {/* Header with AI Icon */}
                  <div className="flex items-center gap-3 mb-4">
                    <img 
                      src="assets/AI-SC.png" 
                      alt="AI Icon" 
                      className="w-10 h-10"
                    />
                    <div>
                      <h2 className="text-[15px] font-semibold text-gray-800 m-0">Column Suggestion</h2>
                      <p className="text-[12px] text-gray-500 m-0 mt-0.5">Below is the suggestion generated</p>
                    </div>
                  </div>

                  {/* Suggestion Cards - White cards inside parent */}
                  <div className="suggestions-scroll max-h-64 space-y-3 mb-4">
                    {suggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        onClick={() => toggleSuggestion(index)}
                        className="p-3 rounded-lg cursor-pointer transition-all bg-white"
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 mt-0.5">
                            <div 
                              className={`w-5 h-5 rounded flex items-center justify-center ${
                                selectedSuggestions.has(index) 
                                  ? "bg-[#3B82F6]" 
                                  : "border-2 border-gray-300 bg-white"
                              }`}
                            >
                              {selectedSuggestions.has(index) && (
                                <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                                  <path 
                                    d="M1 5L4.5 8.5L11 1.5" 
                                    stroke="white" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                            </div>
                          </div>

                          <div className="flex-1">
                            <h3 className="text-[13px] font-semibold text-gray-800 m-0 mb-1">
                              {suggestion.column_name}
                            </h3>
                            <p className="text-[12px] text-gray-500 m-0 leading-relaxed">{suggestion.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Insert Selected Button - Inside the parent card */}
                  <button
                    onClick={handleInsertColumns}
                    disabled={isInserting || selectedSuggestions.size === 0}
                    className={`w-full text-white border-none rounded-lg py-3 px-4 text-[14px] font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
                      isInserting || selectedSuggestions.size === 0
                        ? "bg-gray-400 cursor-not-allowed opacity-60"
                        : "bg-[#068E75] cursor-pointer hover:bg-[#057A63]"
                    }`}
                  >
                    {isInserting ? (
                      <>
                        <div
                          className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                          style={{ animation: "spin 1s linear infinite" }}
                        ></div>
                        Inserting...
                      </>
                    ) : (
                      `Insert Selected (${selectedSuggestions.size})`
                    )}
                  </button>
                </div>

                {error && (
                  <div ref={errorRef} className="bg-red-100 rounded-md p-3 mt-4 mb-4 flex items-start gap-2">
                    <div className="w-5 h-5 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-sm font-bold">!</span>
                    </div>
                    <p className="text-[13px] text-red-800 m-0 leading-snug">
                      {error}
                    </p>
                  </div>
                )}

              </>
            )}
          </div>
        </div>

        {/* Footer Section with Button - Only for Generate state */}
        {suggestions.length === 0 && (
          <div className="w-full px-5 py-3" style={{ backgroundColor: 'rgba(211, 232, 217, 0.5)' }}>
            <div className="w-full max-w-md mx-auto">
              <button
                onClick={handleGenerate}
                disabled={isBusy || !selectedSource || !userIntent.trim()}
                className={`w-full text-white border-none rounded-lg py-3 px-4 text-[14px] font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
                  isBusy || !selectedSource || !userIntent.trim()
                    ? "bg-gray-400 cursor-not-allowed opacity-60"
                    : "bg-[#068E75] cursor-pointer hover:bg-[#057A63]"
                }`}
              >
                {isBusy ? (
                  <>
                    <div
                      className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                      style={{ animation: "spin 1s linear infinite" }}
                    ></div>
                    Generating Suggestions...
                  </>
                ) : (
                  <>
                    <AutoAwesomeIcon sx={{ fontSize: 18 }} />
                    Generate Column Suggestions
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SmartColumn;
